import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Sparkles } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { api } from "@/lib/api";

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 3600) return `${Math.max(1, Math.floor(s / 60))}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export function NotificationBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch {}
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 120000);
    return () => clearInterval(t);
  }, [load]);

  const onOpenChange = async (o) => {
    setOpen(o);
    if (o && unread > 0) {
      try {
        await api.post("/notifications/read");
        setUnread(0);
      } catch {}
    }
  };

  const openItem = (n) => {
    setOpen(false);
    navigate(n.topic_slug ? `/dashboard?open=${n.topic_slug}` : "/dashboard");
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          data-testid="notification-bell"
          className="relative grid place-items-center h-9 w-9 rounded-full hover:bg-accent transition-colors outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          aria-label="Notifications"
        >
          <Bell className="h-5 w-5 text-muted-foreground" />
          {unread > 0 && (
            <span
              data-testid="notification-badge"
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 grid place-items-center rounded-full bg-pink-600 text-white text-[10px] font-bold leading-none"
            >
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" sideOffset={10} className="w-[min(92vw,380px)] p-0 rounded-2xl shadow-xl overflow-hidden">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-border/60 bg-secondary/40">
          <Sparkles className="h-4 w-4 text-primary" />
          <p className="font-heading font-semibold text-sm">Fresh in your path</p>
        </div>
        <div data-testid="notification-list" className="max-h-[60vh] overflow-y-auto">
          {items.length === 0 ? (
            <div data-testid="notification-empty" className="px-4 py-8 text-center">
              <p className="text-sm font-medium text-foreground">Nothing new yet</p>
              <p className="text-xs text-muted-foreground mt-1">
                We scan YouTube daily — when a great new video fits your path, you'll see it here.
              </p>
            </div>
          ) : (
            items.map((n, i) => (
              <button
                key={`${n.topic_slug}-${n.created_at}-${i}`}
                data-testid={`notification-item-${i}`}
                onClick={() => openItem(n)}
                className="w-full flex items-start gap-3 px-4 py-3 text-left border-b border-border/40 last:border-b-0 hover:bg-accent/50 transition-colors"
              >
                <span className={`mt-1 grid place-items-center h-8 w-8 shrink-0 rounded-xl ${n.read ? "bg-secondary text-muted-foreground" : "bg-primary/10 text-primary"}`}>
                  <Sparkles className="h-4 w-4" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-2">
                    <span className={`text-sm font-semibold truncate ${n.read ? "text-muted-foreground" : "text-foreground"}`}>{n.title}</span>
                    {!n.read && <span className="h-2 w-2 shrink-0 rounded-full bg-primary" />}
                  </span>
                  <span className="block text-xs text-muted-foreground line-clamp-2 mt-0.5">{n.body}</span>
                  <span className="block text-[10px] text-muted-foreground/70 mt-1">{timeAgo(n.created_at)}</span>
                </span>
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
